// import { Helmet } from "react-helmet-async";
import { useState } from "react";
// AGGIUNTO: Download da lucide-react per l'icona del book
import { Calendar, MapPin, Users, Clock, ChevronRight, FileText, Phone, Mail, User, AlertTriangle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

/**
 * Componente EventsSection: Visualizza la lista degli eventi attivi,
 * gestisce i popup dei dettagli e i form di iscrizione delle squadre.
 */
export const EventsSection = ({ events, loading, onSubmitApplication }) => {
  // Stati per la gestione della UI (apertura popup e selezione evento)
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  
  // Stato per i dati del modulo di iscrizione
  const [formData, setFormData] = useState({
    team_name: "",
    operators_count: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });
  // Stato per gestire il caricamento durante l'invio del form
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Funzione per calcolare lo stato della deadline (scadenza iscrizioni)
  const getDeadlineStatus = (deadlineStr) => {
    if (!deadlineStr) return { isClosed: false, isImminent: false };
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineStr);
    deadline.setHours(0, 0, 0, 0);
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      isClosed: diffTime < 0,                // Scaduta
      isImminent: diffDays <= 2 && diffDays >= 0, // Mancano 3 giorni o meno
      daysLeft: diffDays
    };
  };  
  
  /**
   * Logica di Filtro e Ordinamento:
   * Filtra gli eventi per mostrare solo quelli futuri (o odierni)
   * e li ordina cronologicamente.
   */
  const activeEvents = events
    .filter(event => {
      const eventDate = new Date(event.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return eventDate >= today;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  /**
   * handleOpenApply:
   * Apre il popup del modulo di iscrizione per l'evento selezionato.
   */
  const handleOpenApply = (event) => {
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };

  /**
   * handleOpenInfo:
   * Apre il popup dei dettagli informativi dell'evento.
   * Cerca l'evento nell'array originale per garantire dati aggiornati.
   */
  const handleOpenInfo = (event) => {
    // Cerchiamo l'evento più aggiornato dall'array originale per sicurezza
    const freshEvent = events.find(e => e.id === event.id) || event;
    setSelectedEvent(freshEvent);
    setIsInfoOpen(true);
  };

  /**
   * handleSubmit:
   * Gestisce l'invio del form di iscrizione.
   * Include validazioni locali sul numero di operatori (min/max)
   * e chiama la funzione di callback onSubmitApplication.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;

    const count = parseInt(formData.operators_count);
    // Validazione specifica per il numero di operatori richiesti dall'evento
    if (typeof selectedEvent.min_operators === 'number' && count < selectedEvent.min_operators) {
      toast.error("Team troppo piccolo", { 
        description: `Il minimo richiesto è di ${selectedEvent.min_operators} operatori.` 
      });
      return;
    }

    if (typeof selectedEvent.max_operators === 'number' && count > selectedEvent.max_operators) {
      toast.error("Team troppo grande", { 
        description: `Il massimo consentito è di ${selectedEvent.max_operators} operatori.` 
      });
      return;
    }

    setIsSubmitting(true);
    // Chiamata all'API (passata tramite props)
    const success = await onSubmitApplication({
      event_id: selectedEvent.id,
      ...formData
    });
    
    // Se l'invio ha successo, chiude il form e resetta i campi
    if (success) {
      setIsDialogOpen(false);
      setFormData({ team_name: "", operators_count: "", contact_name: "", contact_email: "", contact_phone: "" });
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <section id="eventi" className="section-tactical py-12 bg-tactical-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header della Sezione */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-tactical-primary" />
              <span className="font-mono text-xs text-tactical-primary tracking-widest uppercase">Operazioni</span>
            </div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-wider text-tactical-text uppercase">
              EVENTI IN <span className="text-tactical-primary">PROGRAMMA</span>
            </h2>
          </div>

          {/* Vecchia Visualizzazione Skeleton durante il caricamento dei dati */}
          {/*{loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-tactical-bg border border-tactical-border p-6">
                  <Skeleton className="h-48 w-full bg-tactical-surface-highlight" />
                </div>
              ))}*/}
          {loading ? (
            /* MODIFICATO: Skeleton Tattici con testi e animazioni radar */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="relative bg-tactical-bg border border-tactical-border p-8 h-[550px] flex flex-col items-center justify-center overflow-hidden">
                  
                  {/* Effetto Radar Centrale */}
                  <div className="relative w-20 h-20 mb-6">
                    <div className="absolute inset-0 border-2 border-tactical-primary rounded-full animate-ping opacity-20" />
                    <div className="absolute inset-0 border border-tactical-primary rounded-full animate-pulse opacity-40" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Users className="w-8 h-8 text-tactical-primary/30 animate-pulse" />
                    </div>
                  </div>

                  {/* Testi di Caricamento */}
                  <div className="text-center space-y-4 z-10">
                    <p className="font-mono text-[10px] text-tactical-primary tracking-[0.3em] uppercase animate-pulse">
                      Ricerca Operazioni in corso...
                    </p>
                    <div className="space-y-2">
                      <Skeleton className="h-3 w-48 bg-tactical-surface-highlight opacity-40 mx-auto" />
                      <Skeleton className="h-2 w-32 bg-tactical-surface-highlight opacity-20 mx-auto" />
                    </div>
                  </div>

                  {/* Decorazione Tattica Angolare (opzionale) */}
                  <div className="absolute top-4 left-4 w-4 h-4 border-t border-l border-tactical-primary/30" />
                  <div className="absolute bottom-4 right-4 w-4 h-4 border-b border-r border-tactical-primary/30" />
                  
                  {/* Barra di Scansione Orizzontale */}
                  <div className="absolute inset-0 w-full h-full pointer-events-none">
                    <div className="w-full h-[2px] bg-tactical-primary/10 shadow-[0_0_15px_rgba(var(--primary),0.5)] animate-[scan_4s_ease-in-out_infinite]" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Render delle Card degli Eventi */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {activeEvents.map((event) => {
                /**
                 * Logica di determinazione stato Posti:
                 */
                const deadlineStatus = getDeadlineStatus(event.deadline_date);
                const hasMaxTeamsLimit = event.max_teams !== undefined && event.max_teams !== null;
                const max = Number(event.max_teams);
                const confirmed = Number(event.confirmed_count) || 0;
                
                // Situazione A: Posti esauriti
                const isSoldOut = hasMaxTeamsLimit && (max === 0 || (max > 0 && confirmed >= max));
                // Situazione B: Tempo scaduto (ma posti potrebbero esserci ancora)
                const isDeadlinePassed = deadlineStatus.isClosed;
                // L'azione di iscrizione è bloccata in ENTRAMBI i casi
                const canRegister = !isSoldOut && !isDeadlinePassed;
                // Definiamo "Posti Limitati" solo se max > 0 e non siamo ancora in Sold Out
                // const isLimited = hasMaxTeamsLimit && max > 0 && !isSoldOut;
                const isLimited = hasMaxTeamsLimit && max > 0 && !isSoldOut && !isDeadlinePassed;

                // [MODIFICA]: Gestione URL Immagine (Cloudinary o Locale)
                const imageUrl = event.image_url?.startsWith('http') 
                  ? event.image_url 
                  : (event.image_url || "/events/default.png");

                return (
                  <article key={event.id} className="event-card relative bg-tactical-bg border border-tactical-border overflow-hidden group transition-all duration-300 hover:border-tactical-primary/50 flex flex-col h-full">
                    {/* Immagine e Badge Data */}
                    <div className="relative h-64 overflow-hidden flex-shrink-0">
                      <img 
                        src={event.image_url || "/events/default.png"} 
                        className={`w-full h-full object-cover transition-all duration-700 ease-in-out scale-110 group-hover:scale-100 ${isSoldOut ? 'grayscale opacity-40' : 'grayscale group-hover:grayscale-0 saturate-0 group-hover:saturate-[2]'}`} 
                        alt={`Locandina evento: ${event.title}`}
                        loading="lazy"
                      />
                      <div className="absolute top-0 left-0 bg-tactical-primary text-black px-5 py-3 font-mono z-10 shadow-2xl">
                        <time dateTime={event.date} className="flex flex-col items-center leading-none">
                          <span className="text-3xl font-black tracking-tighter">{new Date(event.date).getDate()}</span>
                          <span className="text-xl uppercase font-bold tracking-widest border-t border-black/20 mt-1 pt-1">
                            {new Date(event.date).toLocaleDateString("it-IT", { month: "short" })}
                          </span>
                        </time>
                      </div>
                      {/* Overlay dinamico motivo chiusira visibile sopra l'immagine */}
                      {isSoldOut && (
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                            <span className="bg-red-600 text-white text-3xl font-black px-6 py-3 border-2 border-white rotate-[-10deg] shadow-2xl tracking-tighter">POSTI ESAURITI</span>
                        </div>
                      )}
                      {isDeadlinePassed && !isSoldOut && (
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                            <span className="bg-zinc-700 text-white text-2xl font-black px-6 py-3 border-2 border-zinc-500 rotate-[5deg] shadow-2xl tracking-tighter uppercase">ISCRIZIONI CHIUSE</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-tactical-bg via-transparent to-transparent" />
                    </div>
                    {/* Dettagli Card */}
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="font-heading font-bold text-xl tracking-wider text-tactical-text uppercase mb-4 group-hover:text-tactical-primary transition-colors line-clamp-2 min-h-[3.5rem]">
                        {event.title}
                      </h3>
      
                      <div className="flex flex-col gap-y-3 mb-6 flex-grow">
                        <div className="flex items-center gap-3 text-tactical-text-muted font-mono text-sm uppercase group-hover:text-tactical-text transition-colors">
                          <MapPin className="w-4 h-4 text-tactical-primary flex-shrink-0" /> 
                          <span className="leading-tight line-clamp-1">{event.location}</span>
                        </div>
                        {/* Status Iscrizioni sulla card */}
                        <div className="flex items-center gap-3 text-tactical-text-muted font-mono text-sm uppercase">
                          <Users className="w-4 h-4 text-tactical-primary flex-shrink-0" /> 
                          <span>
                            {isSoldOut ? (
                              <span className="text-red-500 font-bold">OPERAZIONE AL COMPLETO</span>
                            ) : isDeadlinePassed ? (
                              <span className="text-orange-500 font-bold uppercase">Iscrizioni Chiuse</span>
                            ) : isLimited ? (
                              <span className="text-tactical-primary animate-pulse font-bold">[ POSTI LIMITATI ]</span>
                            ) : (
                              "Iscrizioni Aperte"
                            )}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 text-tactical-text-muted font-mono text-sm uppercase">
                          <Clock className="w-4 h-4 text-tactical-primary flex-shrink-0" /> 
                          <span>Orario Inizio {event.time}</span>
                        </div>
                      </div>
                      {/* Pulsanti Azione */}
                      <div className="space-y-3 mt-auto">
                        <Button onClick={() => handleOpenInfo(event)} variant="outline" className="w-full border-tactical-primary/30 text-tactical-text hover:bg-tactical-primary hover:text-black transition-all uppercase text-xs tracking-widest font-bold h-12">
                          <FileText className="w-4 h-4 mr-2" /> Dettagli
                        </Button>
                        <Button 
                          onClick={() => handleOpenApply(event)} 
                          disabled={!canRegister}
                          className={`w-full font-bold uppercase tracking-widest text-xs h-12 transition-colors ${!canRegister ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' : 'bg-tactical-primary text-black hover:bg-white'}`}
                        >
                          {isSoldOut ? "POSTI ESAURITI" : isDeadlinePassed ? "TERMINE SUPERATO" : "Iscrizione"} 
                          {canRegister && <ChevronRight className="w-4 h-4 ml-2" />}
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {/* Messaggio Fallback */}
          {!loading && events.length === 0 && (
            <div className="text-center py-10 border border-dashed border-tactical-border">
              <p className="font-mono text-tactical-text-muted text-sm uppercase">
                Nessuna operazione imminente rilevata dal comando.
              </p>
            </div>
          )}
        </div>

        {/* --- POPUP INFO: Dettagli completi e contatore squadre --- */}
        <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
          <DialogContent className="bg-tactical-bg border-tactical-border max-w-5xl p-0 overflow-hidden border-2 border-tactical-primary/20 flex flex-col md:flex-row h-[90vh] md:h-auto md:max-h-[85vh]">
            {/* Titolo nascosto per i lettori di schermo */}
            <DialogHeader className="sr-only">
              <DialogTitle>{selectedEvent?.title || "Dettagli Evento"}</DialogTitle>
              <DialogDescription>Dettagli operativi e book di gara</DialogDescription>
            </DialogHeader>

            <div className="relative w-full md:w-1/2 h-10 md:h-full bg-black flex-shrink-0">
              {/* [MODIFICA]: Gestione URL Immagine nel popup */}
              <img 
                src={selectedEvent?.image_url?.startsWith('http') ? selectedEvent.image_url : selectedEvent?.image_url} 
                className="w-full h-full object-cover saturate-[1]" 
                alt="Locandina" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-tactical-bg md:hidden" />
            </div>

            <div className="w-full md:w-1/2 p-6 md:p-10 bg-tactical-surface overflow-y-auto flex flex-col text-left">
              <div className="mb-6">
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-tactical-primary uppercase leading-tight">{selectedEvent?.title}</h2>
                <div className="h-1 w-16 bg-tactical-primary mt-2" />
              </div>

              <div className="space-y-6 flex-grow">
                {/* Alert se i posti sono terminati o bloccati manualmente */}
                {selectedEvent && (
                  <>
                    {(Number(selectedEvent.max_teams) > 0 && selectedEvent.confirmed_count >= selectedEvent.max_teams) && (
                      <div className="p-3 bg-red-600/10 border border-red-600/50 text-red-500 flex items-center gap-2 text-sm font-bold uppercase font-mono">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" /> 
                        <span>Posti esauriti per questa operazione. Contatta il comando per informazioni.</span>
                      </div>
                    )}

                    {(getDeadlineStatus(selectedEvent.deadline_date).isClosed && !(Number(selectedEvent.max_teams) > 0 && selectedEvent.confirmed_count >= selectedEvent.max_teams)) && (
                      <div className="p-3 bg-orange-600/10 border border-orange-600/50 text-orange-500 flex items-center gap-2 text-sm font-bold uppercase font-mono">
                        <Clock className="w-5 h-5 flex-shrink-0" /> 
                        <span>Iscrizioni chiuse. Contatta il comando per ulteriori informazioni.</span>
                      </div>
                    )}
                  </>
                )}
                
                <section>
                  <h4 className="font-mono text-xs text-tactical-primary uppercase tracking-[0.3em] mb-3">
                    {selectedEvent?.category || "Dettagli Operativi"}
                  </h4>
                  <div className="font-body text-tactical-text text-base p-4 bg-black/40 border-l-2 border-tactical-primary leading-relaxed whitespace-pre-line">
                    {selectedEvent?.full_description || selectedEvent?.description}
                  </div>
                </section>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 border border-tactical-border bg-black/20">
                    <span className="font-mono text-xs text-tactical-text-muted uppercase block mb-2">Status Posti</span>
                    <span className="text-tactical-text font-bold text-sm uppercase">
                      {selectedEvent?.max_teams !== null && selectedEvent?.max_teams !== undefined ? (
                        Number(selectedEvent.max_teams) === 0 ? (
                          <span className="text-red-500 font-black">Operazione al completo</span>
                        ) : (
                          `${selectedEvent?.confirmed_count || 0} / ${selectedEvent?.max_teams} Squadre Confermate`
                        )
                      ) : (
                        "Iscrizioni Aperte"
                      )}
                    </span>
                  </div>

                  {/* Requisiti Operatori */}
                  {Number(selectedEvent?.min_operators) > 0 && (
                    <div className="p-4 border border-tactical-border bg-black/20">
                      <span className="font-mono text-xs text-tactical-text-muted uppercase block mb-2">Requisiti Operatori</span>
                      <span className="text-tactical-text font-bold text-sm uppercase">
                        {selectedEvent.min_operators} - {selectedEvent.max_operators} per Team
                      </span>
                    </div>
                  )}

                  {/* Box Contributo Operativo */}
                  {selectedEvent?.entry_fee && (
                    <div className="p-4 border border-tactical-primary/20 bg-tactical-primary/5">
                      <span className="font-mono text-xs text-tactical-primary uppercase block mb-2">Contributo Operativo</span>
                      <span className="text-tactical-text font-bold text-sm uppercase">
                        {selectedEvent.entry_fee}
                      </span>
                    </div>
                  )}
                  
                  {/* Box data scadenza iscrizioni */}
                  {selectedEvent?.deadline_date && (
                    <div className={`p-4 border transition-colors ${
                      getDeadlineStatus(selectedEvent.deadline_date).isClosed 
                        ? "border-red-600/50 bg-red-600/10 text-red-500" // Rosso se chiuse
                        : getDeadlineStatus(selectedEvent.deadline_date).daysLeft <= 2 
                          ? "border-yellow-500 bg-yellow-500/10 text-yellow-500" // GIALLO se mancano 2 giorni o meno
                          : "border-tactical-border bg-black/20 text-tactical-text" // Standard
                    }`}>
                      <span className="font-mono text-xs uppercase block mb-2 opacity-70">Chiusura Iscrizioni</span>
                      <span className="font-bold text-sm uppercase">
                        {new Date(selectedEvent.deadline_date).toLocaleDateString("it-IT", { day: '2-digit', month: 'long' })}
                        {/* Opzionale: aggiunge il countdown se mancano pochi giorni */}
                        {!getDeadlineStatus(selectedEvent.deadline_date).isClosed && getDeadlineStatus(selectedEvent.deadline_date).daysLeft <= 2 && (
                          <span className="block text-[10px] mt-1 animate-pulse">- Scade tra {getDeadlineStatus(selectedEvent.deadline_date).daysLeft} giorni -</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* --- AGGIUNTA: AREA PULSANTI FINALI (BOOK + ISCRIZIONE) --- */}
              <div className="space-y-3 mt-8">
                {/* Bottone Scarica Book: Compare solo se book_filename esiste nel database */}
                {selectedEvent?.book_filename && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      //  Logica robusta per l'apertura del PDF (Cloudinary o Locale)
                      const file = selectedEvent.book_filename;
                      const url = file.startsWith('http') ? file : `/events/${file}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    className="w-full border-tactical-primary/30 text-tactical-primary hover:bg-tactical-primary hover:text-black transition-all uppercase text-xs tracking-widest font-bold h-14"
                  >
                    <Download className="w-4 h-4 mr-2" /> Scarica il Book dell'evento
                  </Button>
                )}

                {/* Bottone Iscrizione Squadra */}
                <Button 
                  onClick={() => { setIsInfoOpen(false); setIsDialogOpen(true); }} 
                  disabled={
                    getDeadlineStatus(selectedEvent?.deadline_date).isClosed ||
                    (selectedEvent?.max_teams !== null && selectedEvent?.max_teams !== undefined && selectedEvent?.confirmed_count >= selectedEvent?.max_teams)
                  }
                  className="w-full bg-white text-black hover:bg-tactical-primary font-bold py-8 transition-all uppercase tracking-[0.2em] flex-shrink-0 disabled:opacity-50 disabled:bg-zinc-800 disabled:text-zinc-500 text-sm"
                >
                  { (Number(selectedEvent?.max_teams) > 0 && selectedEvent?.confirmed_count >= selectedEvent?.max_teams)
                    ? "POSTI ESAURITI" 
                    : getDeadlineStatus(selectedEvent?.deadline_date).isClosed
                      ? "ISCRIZIONI CHIUSE"
                      : "Inizia Iscrizione Squadra"
                  }
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* --- DIALOG ISCRIZIONE --- */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-tactical-surface border-tactical-border max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl text-tactical-text uppercase tracking-widest text-left">
                REGISTRAZIONE <span className="text-tactical-primary">SQUADRA</span>
              </DialogTitle>
              <DialogDescription className="text-tactical-text-muted text-sm uppercase font-mono text-left">
                Inserisci i dati del team per partecipare.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4 text-left">
              <div className="space-y-2">
                <Label className="text-xs uppercase text-tactical-text-muted font-mono font-bold">Nome Team / Club *</Label>
                <Input value={formData.team_name} onChange={(e) => setFormData({...formData, team_name: e.target.value})} required className="bg-black/50 border-tactical-border text-tactical-text h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-tactical-text-muted font-mono font-bold">
                  Numero Operatori {selectedEvent?.min_operators && `(Range: ${selectedEvent.min_operators}-${selectedEvent.max_operators})`} *
                </Label>
                <Input type="number" min={selectedEvent?.min_operators || 1} max={selectedEvent?.max_operators} value={formData.operators_count} onChange={(e) => setFormData({...formData, operators_count: e.target.value})} required className="bg-black/50 border-tactical-border text-tactical-text h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-tactical-text-muted font-mono font-bold">Nome Referente *</Label>
                <Input type="text" value={formData.contact_name} onChange={(e) => setFormData({...formData, contact_name: e.target.value})} required className="bg-black/50 border-tactical-border text-tactical-text h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-tactical-text-muted font-mono font-bold">Email Referente *</Label>
                <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} required className="bg-black/50 border-tactical-border text-tactical-text h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-tactical-text-muted font-mono font-bold">Telefono Referente *</Label>
                <Input type="tel" value={formData.contact_phone} onChange={(e) => setFormData({...formData, contact_phone: e.target.value})} required className="bg-black/50 border-tactical-border text-tactical-text h-12" />
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full bg-tactical-primary text-black font-bold uppercase py-8 text-sm tracking-widest hover:bg-white transition-colors mt-4">
                {isSubmitting ? "TRASMISSIONE IN CORSO..." : "INVIA RICHIESTA"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </section>
    </>
  );
};